import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Save, Loader2, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

const fields = [
  { key: "hero_eyebrow", label: "Hero eyebrow", placeholder: "Approved by the State of Florida" },
  { key: "hero_title", label: "Hero title", placeholder: "Our Overdose Awareness license plate has been approved.", multiline: true },
  { key: "hero_body", label: "Hero body copy", placeholder: "Help us reach 3,000 pre-sold vouchers at $33 each...", multiline: true },
  { key: "hero_primary_label", label: "Hero primary button label", placeholder: "Get Your Voucher" },
  { key: "hero_primary_href", label: "Hero primary button link", placeholder: "/overdose-awareness-license-plates" },
  { key: "mission_eyebrow", label: "Mission eyebrow", placeholder: "Our Mission" },
  { key: "mission_title_line1", label: "Mission title - line 1", placeholder: "Changing Minds," },
  { key: "mission_title_line2", label: "Mission title - line 2 (italic)", placeholder: "Changing Lives." },
  { key: "mission_body_1", label: "Mission body - paragraph 1", multiline: true, placeholder: "Stigma is a public health issue..." },
  { key: "mission_body_2", label: "Mission body - paragraph 2", multiline: true, placeholder: "According to WHO..." },
  { key: "quote_text", label: "Quote text", multiline: true, placeholder: "Change the story and you change perception..." },
  { key: "quote_author", label: "Quote author", placeholder: "Jean Houston" },
  { key: "final_cta_title", label: "Final CTA title", placeholder: "Stand with us. Reverse the stigma." },
  { key: "final_cta_body", label: "Final CTA body", multiline: true, placeholder: "Whether you're a person in recovery..." },
];

export default function AdminHomeContent() {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get("/site-content/home");
        setData(d || {});
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/site-content/home", data);
      toast.success("Home page content saved.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const upd = (k, v) => setData({ ...data, [k]: v });
  const cls = "w-full border-2 border-[var(--rts-blue-pale)] focus:border-[var(--rts-blue)] rounded-xl px-3 py-2.5 outline-none min-h-[44px]";

  if (loading) return <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[var(--rts-blue-deep)]" /></div>;

  return (
    <div data-testid="admin-home-content">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display text-3xl text-[var(--rts-ink)]">Home Page Content</h1>
          <p className="text-sm text-[var(--rts-muted)] mt-1">
            Edit the landing page hero, mission section, and quote. Leave a field blank to keep the current site text.
          </p>
          <Link to="/" target="_blank" className="inline-flex items-center gap-1 mt-2 text-xs font-semibold text-[var(--rts-blue-deep)] hover:underline">
            <ExternalLink className="w-3 h-3" /> View live home page
          </Link>
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="home-save-btn"
          className="inline-flex items-center gap-2 bg-[var(--rts-blue-deep)] hover:bg-[var(--rts-blue)] text-white font-bold px-6 py-2.5 rounded-full disabled:opacity-60 min-h-[44px]"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save changes
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-[var(--rts-blue-pale)] p-6 space-y-5">
        {fields.map((f) => (
          <label key={f.key} className="block">
            <span className="block text-sm font-semibold text-[var(--rts-ink)] mb-1">{f.label}</span>
            {f.multiline ? (
              <textarea
                rows={f.rows || 3}
                value={data[f.key] ?? ""}
                onChange={(e) => upd(f.key, e.target.value)}
                placeholder={f.placeholder}
                className={cls + " resize-y"}
                data-testid={`home-field-${f.key}`}
              />
            ) : (
              <input
                type="text"
                value={data[f.key] ?? ""}
                onChange={(e) => upd(f.key, e.target.value)}
                placeholder={f.placeholder}
                className={cls}
                data-testid={`home-field-${f.key}`}
              />
            )}
            {f.placeholder && !data[f.key] && (
              <p className="text-xs text-[var(--rts-muted)] mt-1">Site currently shows: <span className="italic">{f.placeholder}</span></p>
            )}
          </label>
        ))}
      </div>
    </div>
  );
}
